// @givmo/fundraisers-module — one fundraiser's QR design page (FCM-WEB4; plan §FC-14).
//
// Opened from the share panel's "Customize" at `/fundraisers/$fundraiserId/qr` (the `campaign`
// child precedent). It frames the ONE shared editor, `OrgQrDesignEditor` (`@givmo/org-console`), on
// this fundraiser's own code: the preview and every download are this fundraiser's canonical link
// drawn in the WORKING design, and only "Save as default" changes the organization's design — which
// then styles every QR code the organization generates, this one included.
//
// Who may save is `CAPABILITY_MANAGE_ORG_QR` off the host's ONE standing read (`config.capabilities`,
// failing closed while unreported); anyone who can read the fundraiser may style a copy and download
// it. The page serves only while the host reveals the editor (`config.qrDesign`), and only for a
// fundraiser whose share code exists — an active one.
//
// ⚠ THE SUBTITLE SPEAKS FOR THE EDITOR, SO IT GOES WHEN THE DESIGN IS NOT THERE. With the backend's
// organization QR design dark, the design read answers the route miss and the editor says the design
// isn't available here; a subtitle inviting the person to choose or style one above that would be a
// second, contradicting answer. The page reads the design through the editor's own query key, so the
// two share ONE request.

import { useQuery } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, EmptyState, Icon, PageHeader } from '@givmo/design-system';
import {
  CAPABILITY_MANAGE_ORG_QR,
  OrgQrDesignEditor,
  holdsCapability,
  orgQrDesignOptions,
} from '@givmo/org-console';
import { ArrowLeft } from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { mayReadFundraisers } from './fundraisers-config';
import { fundraiserOptions } from './fundraisers-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import {
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
  ModuleLink,
} from './fundraisers-state';

export const FUNDRAISER_QR_COPY = {
  title: 'QR code design',
  canSave: (orgName: string) =>
    `Choose how ${orgName} QR codes look. The preview is this fundraiser’s code.`,
  cannotSave: 'Style a copy of this fundraiser’s QR code to download.',
  notActive: 'This fundraiser’s QR code is ready once it’s published.',
  closed: 'This fundraiser is closed, so its QR code can’t be styled here.',
};

export function FundraiserQrDesignPage({
  fundraiserId,
  orgLogoSrc = null,
}: {
  fundraiserId: string;
  /** The organization's logo for the editor's logo choice — the host already reads it. */
  orgLogoSrc?: string | null;
}) {
  const config = useFundraisersModule();
  const readable = mayReadFundraisers(config);
  const detail = useQuery({
    ...fundraiserOptions(config.orgRef ?? '', fundraiserId),
    enabled: readable && config.qrDesign != null,
  });
  const design = useQuery({
    ...orgQrDesignOptions(config.orgRef ?? ''),
    enabled:
      readable &&
      config.qrDesign != null &&
      config.orgRef !== null &&
      detail.data?.state === 'active',
  });
  const designAbsent = isGivmoError(design.error) && design.error.status === 404;
  const canSave = holdsCapability(config.capabilities ?? null, CAPABILITY_MANAGE_ORG_QR);
  const back = (
    <div>
      <Button
        appearance="saas"
        tone="ghost"
        size="sm"
        leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
        onClick={() => config.onNavigate(FUNDRAISERS_ROUTES.editor(fundraiserId))}
        testId="fundraiser-qr-back"
      >
        {detail.data?.name ?? 'Fundraiser'}
      </Button>
    </div>
  );

  if (!config.qrDesign) {
    return (
      <FundraisersPageShell testId="page-fundraiser-qr">
        <EmptyState
          testId="fundraiser-qr-not-found"
          title="This page isn’t available"
          action={
            <ModuleLink href={FUNDRAISERS_ROUTES.list} testId="fundraiser-qr-back-to-list">
              Back to Fundraisers
            </ModuleLink>
          }
        />
      </FundraisersPageShell>
    );
  }
  if (!config.orgRef || (readable && detail.isPending)) {
    return (
      <FundraisersPageShell testId="page-fundraiser-qr">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  }
  if (!readable || detail.isError || !detail.data) {
    return (
      <FundraisersPageShell testId="page-fundraiser-qr">
        <FundraisersReadState
          denied={!readable}
          detail
          fundraiserId={fundraiserId}
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      </FundraisersPageShell>
    );
  }
  const row = detail.data;
  return (
    <FundraisersPageShell testId="page-fundraiser-qr">
      {back}
      <PageHeader
        title={FUNDRAISER_QR_COPY.title}
        subtitle={
          designAbsent
            ? undefined
            : canSave
              ? FUNDRAISER_QR_COPY.canSave(config.orgName)
              : FUNDRAISER_QR_COPY.cannotSave
        }
      />
      {row.state === 'active' ? (
        <OrgQrDesignEditor
          orgRef={config.orgRef}
          target={{ kind: 'fundraiser', fundraiserId: row.id }}
          canSave={canSave}
          vocab={{
            orgName: config.orgName,
            saveScope: config.qrDesign.saveScope,
            saveUnavailable: config.qrDesign.saveUnavailable,
          }}
          orgLogoSrc={orgLogoSrc}
          testIdPrefix={config.testIdPrefix}
        />
      ) : (
        <EmptyState
          testId="fundraiser-qr-not-active"
          title={row.state === 'closed' ? FUNDRAISER_QR_COPY.closed : FUNDRAISER_QR_COPY.notActive}
          action={
            <ModuleLink href={FUNDRAISERS_ROUTES.editor(row.id)} testId="fundraiser-qr-open-editor">
              Back to the fundraiser
            </ModuleLink>
          }
        />
      )}
    </FundraisersPageShell>
  );
}
